import { useEffect, useRef, useState } from 'react';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const FULL = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/** Calendar-style month + year picker. Value is `YYYY-MM`. */
export function PeriodPicker({
  value,
  onChange,
  placeholder = 'Pick a month',
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const parsed = /^(\d{4})-(\d{1,2})$/.exec(value);
  const selYear = parsed ? Number(parsed[1]) : null;
  const selMonth = parsed ? Number(parsed[2]) : null;
  const [viewYear, setViewYear] = useState(selYear ?? new Date().getFullYear());

  useEffect(() => {
    if (selYear) setViewYear(selYear);
  }, [selYear]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm outline-none transition hover:border-neutral-400 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 dark:border-neutral-700 dark:bg-neutral-950 dark:hover:border-neutral-600"
      >
        <span className={`flex items-center gap-2 ${parsed ? '' : 'text-neutral-400'}`}>
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
            className="text-emerald-500"
          >
            <rect
              x="3"
              y="4.5"
              width="18"
              height="16"
              rx="2.5"
              stroke="currentColor"
              strokeWidth="2"
            />
            <path
              d="M3 9h18M8 2.5v4M16 2.5v4"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
          {parsed && selMonth ? `${FULL[selMonth - 1]} ${selYear}` : placeholder}
        </span>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M7 10l5 5 5-5"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open && (
        <div className="absolute z-50 mt-2 w-64 rounded-xl border border-neutral-200 bg-white p-3 shadow-xl dark:border-neutral-700 dark:bg-neutral-900">
          <div className="mb-2 flex items-center justify-between">
            <button
              type="button"
              onClick={() => setViewYear((y) => y - 1)}
              className="rounded-md px-2 py-1 text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800"
              aria-label="Previous year"
            >
              ‹
            </button>
            <span className="text-sm font-semibold">{viewYear}</span>
            <button
              type="button"
              onClick={() => setViewYear((y) => y + 1)}
              className="rounded-md px-2 py-1 text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800"
              aria-label="Next year"
            >
              ›
            </button>
          </div>
          <div className="grid grid-cols-3 gap-1.5">
            {MONTHS.map((m, i) => {
              const isSel = selYear === viewYear && selMonth === i + 1;
              return (
                <button
                  key={m}
                  type="button"
                  onClick={() => {
                    onChange(`${viewYear}-${String(i + 1).padStart(2, '0')}`);
                    setOpen(false);
                  }}
                  className={`rounded-lg px-2 py-2 text-sm font-medium transition ${
                    isSel
                      ? 'bg-emerald-600 text-white'
                      : 'text-neutral-600 hover:bg-emerald-50 hover:text-emerald-700 dark:text-neutral-300 dark:hover:bg-emerald-950/40'
                  }`}
                >
                  {m}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
